import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { 
  Home,
  LayoutDashboard, 
  CalendarDays, 
  MessageSquare, 
  Settings, 
  Star, 
  Heart,
  Sliders,
  LogOut,
  Award
} from "lucide-react";

export const GuestSidebar = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: loyaltyPoints } = useQuery({
    queryKey: ["loyalty-points"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("loyalty_points")
        .select("points, tier")
        .eq("user_id", user.id)
        .single();

      if (error) throw error;
      return data;
    }
  });

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Signed out successfully",
        description: "You have been logged out",
      });
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        title: "Error",
        description: "There was a problem signing out",
        variant: "destructive",
      });
    }
  };

  const menuItems = [
    {
      icon: <Home className="h-4 w-4" />,
      label: "Home",
      href: "/",
    },
    {
      icon: <LayoutDashboard className="h-4 w-4" />,
      label: "Dashboard",
      href: "/guest",
    },
    {
      icon: <CalendarDays className="h-4 w-4" />,
      label: "My Bookings",
      href: "/guest/bookings",
    },
    {
      icon: <Heart className="h-4 w-4" />,
      label: "Wishlists",
      href: "/guest/wishlists",
    },
    {
      icon: <MessageSquare className="h-4 w-4" />,
      label: "Messages",
      href: "/guest/messages",
    },
    {
      icon: <Star className="h-4 w-4" />,
      label: "Reviews",
      href: "/guest/reviews",
    },
    {
      icon: <Sliders className="h-4 w-4" />,
      label: "Preferences",
      href: "/guest/preferences",
    },
    {
      icon: <Settings className="h-4 w-4" />,
      label: "Settings",
      href: "/guest/settings",
    },
  ];

  return (
    <div className="space-y-2 p-4 md:p-0">
      {loyaltyPoints && (
        <div className="mb-6 p-4 bg-primary/5 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Award className="h-4 w-4 text-primary" />
            <span className="font-medium">{loyaltyPoints.tier}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {loyaltyPoints.points} points earned
          </p>
        </div>
      )}

      {menuItems.map((item) => (
        <Button
          key={item.href}
          variant="ghost"
          className="w-full justify-start"
          onClick={() => navigate(item.href)}
        >
          {item.icon}
          <span className="ml-2">{item.label}</span>
        </Button>
      ))}
      
      <Button
        variant="ghost"
        className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-100"
        onClick={handleLogout}
      >
        <LogOut className="h-4 w-4" />
        <span className="ml-2">Sign Out</span>
      </Button>
    </div>
  );
};